import { useState, useRef, useCallback, useEffect } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from '../utils/cloudFunctions';

const useScreenComposerBackground = (userId, onComplete) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const pollingRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const successTimerRef = useRef(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const downloadAndUpload = useCallback(async (jobId, outputFormat) => {
    try {
      setProgress('downloading');
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.screenComposerProxy(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'download', jobId }),
      });

      const data = await response.json();
      if (data.success && data.data) {
        const contentType = data.contentType || (outputFormat === 'video' ? 'image/gif' : 'image/jpeg');
        const ext = contentType.includes('gif') ? 'gif' : contentType.includes('png') ? 'png' : 'jpg';
        const byteString = atob(data.data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: contentType });

        const fileRef = storageRef(storage, `screenComposer/${userId}/generated_${Date.now()}.${ext}`);
        await uploadBytes(fileRef, blob);
        const url = await getDownloadURL(fileRef);

        if (onCompleteRef.current) {
          onCompleteRef.current({ type: 'url', url, contentType });
        }

        setJustCompleted(true);
        successTimerRef.current = setTimeout(() => setJustCompleted(false), 3000);
      } else {
        throw new Error('No data in download response');
      }
    } catch (err) {
      setError('Failed to process result: ' + err.message);
    } finally {
      setIsGenerating(false);
      setProgress('');
    }
  }, [userId]);

  const startPolling = useCallback((jobId, format) => {
    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) {
        clearInterval(pollingRef.current);
        setError('Generation timed out. Please try again.');
        setIsGenerating(false);
        setProgress('');
        return;
      }

      try {
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.screenComposerProxy(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'poll', jobId }),
        });

        const data = await response.json();

        if (data.status === 'completed') {
          clearInterval(pollingRef.current);
          await downloadAndUpload(jobId, data.output_format || format);
        } else if (data.status === 'failed') {
          clearInterval(pollingRef.current);
          setError('Generation failed: ' + (data.error || 'Unknown reason'));
          setIsGenerating(false);
          setProgress('');
        }
      } catch (pollErr) {
        if (attempts > 5) {
          clearInterval(pollingRef.current);
          setError('Connection failed: ' + pollErr.message);
          setIsGenerating(false);
          setProgress('');
        }
      }
    }, 2000);
  }, [downloadAndUpload]);

  const startGeneration = useCallback(async ({ url, format, photoUrl, videoUrl }) => {
    setIsGenerating(true);
    setProgress('starting');
    setError(null);
    setJustCompleted(false);

    try {
      const targetUrl = CLOUD_FUNCTION_URLS.screenComposerProxy();
      const payload = {
        action: 'generate',
        url,
        format,
        photo_url: photoUrl,
        video_url: videoUrl,
      };

      const response = await authenticatedFetch(targetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Invalid response from server'); }
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      if (!data.job_id) throw new Error('No job_id in response');

      setProgress('generating');
      startPolling(data.job_id, format);
    } catch (err) {
      setError(err.message || 'Failed to start generation');
      setIsGenerating(false);
      setProgress('');
    }
  }, [startPolling]);

  const dismiss = useCallback(() => {
    setError(null);
    setJustCompleted(false);
  }, []);

  const cancel = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setIsGenerating(false);
    setProgress('');
    setError(null);
  }, []);

  return { isGenerating, progress, error, justCompleted, startGeneration, dismiss, cancel };
};

export default useScreenComposerBackground;
